/**
 * Hook for fetching and managing projects.
 */

import { gql } from "@apollo/client";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  DELETE_PROJECT,
  CREATE_PROJECT_ROLE,
  UPDATE_PROJECT_ROLE,
  DELETE_PROJECT_ROLE,
} from "@/graphql/mutations";
import type { ProjectWithPlots, ProjectConnection } from "@/types";

const GET_PROJECTS = gql`
  query GetProjects($limit: Int, $offset: Int) {
    projects(limit: $limit, offset: $offset) {
      items {
        id
        name
        description
        status
        projectType
        startDate
        objectiveEndDate
        externalQualityInspectionPlatformProjectId
        externalSchedulingPlatformProjectId
        effectiveCongridProjectId
        viewerCapabilities
        version
        createdAt
        updatedAt
        tyopaallikko {
          id
          firstName
          lastName
        }
        vastaavaTyonjohtaja {
          id
          firstName
          lastName
        }
        plots {
          id
          projectId
          name
          headsectionStats {
            headsectionNumber
            label
            total
            completed
            pending
            inProgress
            problem
            late
            completedPercent
            pendingPercent
            inProgressPercent
            problemPercent
            latePercent
          }
        }
      }
      totalCount
      hasNextPage
    }
  }
`;

const CREATE_PROJECT = gql`
  mutation CreateProject($input: ProjectCreateInput!) {
    createProject(input: $input) {
      success
      message
      project {
        id
        name
        description
        status
        projectType
        startDate
        objectiveEndDate
        version
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($input: ProjectUpdateInput!) {
    updateProject(input: $input) {
      success
      message
      project {
        id
        name
        description
        status
        projectType
        startDate
        objectiveEndDate
        version
        createdAt
        updatedAt
      }
    }
  }
`;

interface ProjectsQueryResult {
  projects: ProjectConnection;
}

interface ProjectCreateInput {
  name: string;
  description?: string;
  status: string;
  projectType: string;
  startDate?: string;
  objectiveEndDate?: string;
}

interface ProjectUpdateInput {
  id: string;
  version: number;
  name?: string;
  description?: string;
  status?: string;
  projectType?: string;
  startDate?: string;
  objectiveEndDate?: string;
}

export const GET_PROJECT_ROLES = gql`
  query GetProjectRoles($filter: ProjectRoleFilterInput, $limit: Int) {
    projectRoles(filter: $filter, limit: $limit) {
      items {
        id
        userId
        projectId
        roleType
        isSubstitutionDerived
        isManuallyAssigned
        version
        createdAt
      }
      totalCount
    }
  }
`;

export function useProjects() {
  const { data, loading, error, refetch } = useQuery<ProjectsQueryResult>(
    GET_PROJECTS,
    {
      variables: { limit: 100, offset: 0 },
      fetchPolicy: "cache-and-network",
    },
  );

  const [createProjectMutation] = useMutation(CREATE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [updateProjectMutation] = useMutation(UPDATE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [deleteProjectMutation] = useMutation(DELETE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [createProjectRoleMutation] = useMutation(CREATE_PROJECT_ROLE, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [updateProjectRoleMutation] = useMutation(UPDATE_PROJECT_ROLE, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [deleteProjectRoleMutation] = useMutation(DELETE_PROJECT_ROLE, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [fetchProjectRoles] = useLazyQuery(GET_PROJECT_ROLES, {
    fetchPolicy: "network-only",
  });

  const createProject = async (input: ProjectCreateInput) => {
    const result = await createProjectMutation({ variables: { input } });
    return result.data?.createProject;
  };

  const updateProject = async (input: ProjectUpdateInput) => {
    const result = await updateProjectMutation({ variables: { input } });
    const payload = result.data?.updateProject;
    // The mutation returns success=false (HTTP 200, no GraphQL error) on
    // failures such as an authorization denial or optimistic-lock clash, so
    // throw to surface it to the caller instead of silently appearing to save.
    if (payload && payload.success === false) {
      throw new Error(payload.message || "Hankkeen tallennus epäonnistui");
    }
    return payload;
  };

  const deleteProject = async (id: string) => {
    const result = await deleteProjectMutation({ variables: { id } });
    return result.data?.deleteProject as
      | { success: boolean; message: string }
      | undefined;
  };

  const getProjectRoles = async (projectId: string) => {
    const result = await fetchProjectRoles({
      variables: { filter: { projectId }, limit: 50 },
    });
    const items = (result.data?.projectRoles?.items || []) as Array<{
      id: string;
      userId: string;
      projectId: string;
      roleType: string;
      version: number;
      isSubstitutionDerived: boolean;
      isManuallyAssigned: boolean;
    }>;
    // Hide substitution-derived roles from the management view: they are
    // system-owned (recreated by refresh_substitutes_task) and must not be
    // hand-edited. They still count for access — myProjectIds/visibility read
    // the same query without this filter.
    return items.filter((r) => !r.isSubstitutionDerived);
  };

  const createProjectRole = async (input: {
    userId: string;
    projectId: string;
    roleType: string;
  }) => {
    const result = await createProjectRoleMutation({ variables: { input } });
    const payload = result.data?.createProjectRole;
    // The mutation returns success=false (HTTP 200, no GraphQL error) on
    // failures such as a unique-constraint clash, so throw to surface it to
    // the caller instead of silently dropping the role.
    if (payload && payload.success === false) {
      throw new Error(payload.message || "Roolin luonti epäonnistui");
    }
    return payload;
  };

  const updateProjectRole = async (input: {
    id: string;
    version: number;
    roleType?: string;
  }) => {
    const result = await updateProjectRoleMutation({ variables: { input } });
    return result.data?.updateProjectRole;
  };

  const deleteProjectRole = async (id: string) => {
    const result = await deleteProjectRoleMutation({ variables: { id } });
    const payload = result.data?.deleteProjectRole;
    if (payload && payload.success === false) {
      throw new Error(payload.message || "Roolin poisto epäonnistui");
    }
    return payload;
  };

  return {
    projects: (data?.projects.items || []) as ProjectWithPlots[],
    totalCount: data?.projects.totalCount || 0,
    hasNextPage: data?.projects.hasNextPage || false,
    loading,
    error,
    refetch,
    createProject,
    updateProject,
    deleteProject,
    getProjectRoles,
    createProjectRole,
    updateProjectRole,
    deleteProjectRole,
    fetchProjectsQuery: GET_PROJECTS,
  };
}
